/**
 * Who may hold an identity in this system.
 *
 * Sign-in is Google Workspace. Two facts drive everything here:
 *
 * 1. Enabling Google sign-in in Firebase admits **every Google account on
 *    earth** by default, not just the school's. The `hd` parameter that appears
 *    to restrict it is a UI hint sent by the browser — anyone editing the
 *    request bypasses it. Domain enforcement must happen server-side.
 *
 * 2. A valid school address is still not membership. The project owner's
 *    decision: HR imports the ~800 employees before launch, and anyone signing
 *    in without a matching employee record is refused outright. Otherwise every
 *    new hire — and every service account on the domain — becomes a fund member
 *    the moment they sign in.
 *
 * [ABSOLUTE] The predicates below are for UI messaging. The authority is
 * `firestore.rules` plus Cloud Functions, which repeat these checks against the
 * verified token. A `true` here grants nothing (§7.1).
 */

/** The school's Google Workspace domain. Mirrored in firestore.rules. */
export const WORKSPACE_DOMAIN = 'rasheed.school';

export interface SignInIdentity {
  readonly email: string | null;
  /**
   * Google's own verification of the address. An unverified address must never
   * be treated as proof of domain membership.
   */
  readonly emailVerified: boolean;
}

export function normaliseEmail(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Whether an address belongs to the workspace domain.
 *
 * Matches on the segment after the final `@` rather than with `endsWith`:
 * `attacker@evil-rasheed.school` ends with the domain string, and
 * `rasheed.school@gmail.com` contains it. Neither is a school address.
 */
export function belongsToWorkspaceDomain(email: string): boolean {
  const normalised = normaliseEmail(email);
  const atIndex = normalised.lastIndexOf('@');
  if (atIndex === -1) return false;
  return normalised.slice(atIndex + 1) === WORKSPACE_DOMAIN;
}

export type SignInRefusal = 'no_email' | 'email_unverified' | 'wrong_domain';

/**
 * Domain-level check only. Passing this means the person may *attempt* to use
 * the system; whether an employee record exists is a separate lookup, because
 * that decision belongs to data rather than to the token.
 */
export function refuseSignInReason(identity: SignInIdentity): SignInRefusal | null {
  if (!identity.email) return 'no_email';
  if (!identity.emailVerified) return 'email_unverified';
  if (!belongsToWorkspaceDomain(identity.email)) return 'wrong_domain';
  return null;
}

export const SIGN_IN_REFUSAL_MESSAGES_AR: Readonly<Record<SignInRefusal, string>> = {
  no_email: 'لا يوجد بريد إلكتروني مرتبط بهذا الحساب.',
  email_unverified: 'البريد الإلكتروني غير موثّق. استخدم حساب المدرسة الرسمي.',
  wrong_domain: `الدخول مقتصر على حسابات ‎@${WORKSPACE_DOMAIN}‎ الرسمية.`,
};

/** Shown when the address is valid but HR has not imported the person yet. */
export const NO_EMPLOYEE_RECORD_MESSAGE_AR =
  'لا يوجد سجل موظف مرتبط بهذا الحساب. يرجى مراجعة الموارد البشرية.';
